/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * W22(b) — WHAT THE PER-EXERCISE EDITOR ACTUALLY WRITES.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `EVERFIT-MAP` §2b-ter: clicking an exercise tile on a day card opens a floating editor with a
 * `Set · Weight · Reps · Rest` table. This is the rule that turns what a coach typed into the
 * rows to send — and, more importantly, into the rows NOT to send.
 *
 * 🔴 **ONLY WHAT CHANGED IS WRITTEN, AND THAT IS THE WHOLE DESIGN.** Sending every row on every
 * save means a coach who opened the window and closed it has written to their client's training;
 * the `updated_at` of rows nobody touched moves, and any future "what did the coach change this
 * week" reader is looking at noise. A no-op save must be a NO-OP.
 *
 * ⚠️ **AN EMPTY FIELD IS `null`, WHICH IS A REAL AND DIFFERENT VALUE FROM `0`.** `weight_kg` null
 * is "nobody recorded it" and 0 is "unloaded" — the codex's own line, about this very column.
 * So a cleared box clears the column and never writes a zero.
 *
 * ⚠️ **AND A FIELD THAT DOES NOT PARSE IS A REFUSAL, NEVER A SILENT SKIP.** `"abc"` in the reps
 * box must stop the save with a sentence, because skipping it saves the OTHER fields and leaves
 * the coach believing all of it landed.
 *
 * ⚠️ THIS MODULE IS PURE. The write itself is three lines at the call site; what is worth testing
 * is the decision, which is here.
 */

export type SetDraftRow = {
  /**
   * A stable client-side key. For a stored row it IS the database id; for one the coach has just
   * added it is a local key and `id` is null.
   *
   * ⚠️ IT IS NOT THE INDEX. React keys off this, and an index key on a list a coach can delete
   * from the MIDDLE hands the next row the departed row's state — the classic form-reuse bug,
   * and here it would be a coach's typed weight appearing on a different set.
   */
  key: string;
  /** `null` for a row that does not exist in the database yet. */
  id: string | null;
  reps: string;
  weightKg: string;
  restSeconds: string;
  /** W22(b2) — a TIMED set's duration, `coach_workout_sets.time_seconds`. */
  timeSeconds: string;
  /** Carried, never edited here: a new row copies the last one's type. */
  setType: string;
};

/** The database's own shape for one row, as the door returned it. */
export type SetStored = {
  id: string;
  reps: number | null;
  weight_kg: number | null;
  rest_seconds: number | null;
  time_seconds: number | null;
  set_type: string;
  position: number;
};

export type SetPatch = {
  id: string;
  reps?: number | null;
  weight_kg?: number | null;
  rest_seconds?: number | null;
  time_seconds?: number | null;
};

/** A row to CREATE. `position` is derived here so the caller cannot invent one. */
export type SetInsert = {
  position: number;
  set_type: string;
  reps: number | null;
  weight_kg: number | null;
  rest_seconds: number | null;
  time_seconds: number | null;
};

export type EditPlan =
  | { kind: "refused"; message: string }
  | { kind: "nothing" }
  | { kind: "write"; patches: SetPatch[]; inserts: SetInsert[]; deletes: string[] };

/**
 * A typed number, or a refusal.
 *
 * ⚠️ **BLANK IS `null` AND IS NOT AN ERROR.** ⚠️ **A NEGATIVE IS.** Nobody performs −2 reps, and a
 * minus typed by accident in a weight box is the difference between 20 kg and a row a phone
 * cannot render.
 */
export function parseField(raw: string, label: string): { ok: true; value: number | null } | { ok: false; message: string } {
  const s = raw.trim();
  if (s === "") return { ok: true, value: null };
  // ⚠️ `Number("")` IS 0 AND `Number(" ")` IS 0 — both are handled above, deliberately, because
  // this is the coercion that turns a cleared box into an unloaded set.
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, message: `${label} is not a number.` };
  if (n < 0) return { ok: false, message: `${label} cannot be negative.` };
  return { ok: true, value: n };
}

export function editPlan(before: readonly SetStored[], after: readonly SetDraftRow[]): EditPlan {
  const byId = new Map(before.map((s) => [s.id, s]));
  const patches: SetPatch[] = [];
  const inserts: SetInsert[] = [];
  /* 🔴 POSITIONS ARE TAKEN FROM WHAT THE DATABASE ALREADY HOLDS, NEVER FROM THE DRAFT'S INDEX.
     A coach who deletes set 2 of 4 and adds one leaves rows at positions 0, 2, 3 — which SORT
     correctly and are what the door returns. Renumbering would rewrite two rows nobody touched
     to make the numbers tidy, which is a write the coach did not ask for; and numbering a new
     row by its index in the draft would collide with a row still sitting at that position. */
  let nextPosition = before.reduce((m, s) => Math.max(m, s.position), -1) + 1;
  for (const row of after) {
    const reps = parseField(row.reps, "Reps");
    if (!reps.ok) return { kind: "refused", message: reps.message };
    const weight = parseField(row.weightKg, "Weight");
    if (!weight.ok) return { kind: "refused", message: weight.message };
    const rest = parseField(row.restSeconds, "Rest");
    if (!rest.ok) return { kind: "refused", message: rest.message };
    const time = parseField(row.timeSeconds, "Time");
    if (!time.ok) return { kind: "refused", message: time.message };

    if (row.id === null) {
      inserts.push({
        position: nextPosition,
        set_type: row.setType,
        reps: reps.value, weight_kg: weight.value,
        rest_seconds: rest.value, time_seconds: time.value,
      });
      nextPosition += 1;
      continue;
    }

    const was = byId.get(row.id);
    // A row the editor holds that the database does not is not a row this rule may invent.
    if (was === undefined) return { kind: "refused", message: "A set went missing while you were editing. Close and open it again." };
    const patch: SetPatch = { id: row.id };
    let moved = false;
    if (reps.value !== was.reps) { patch.reps = reps.value; moved = true; }
    if (weight.value !== was.weight_kg) { patch.weight_kg = weight.value; moved = true; }
    if (rest.value !== was.rest_seconds) { patch.rest_seconds = rest.value; moved = true; }
    if (time.value !== was.time_seconds) { patch.time_seconds = time.value; moved = true; }
    if (moved) patches.push(patch);
  }
  /* WHAT THE DRAFT NO LONGER HOLDS IS WHAT THE COACH REMOVED. Derived rather than flagged: a
     `removed: true` field is a second statement of the same fact, and the two drift the first
     time a row is removed by some other route. */
  const kept = new Set(after.flatMap((r) => (r.id === null ? [] : [r.id])));
  const deletes = before.flatMap((s) => (kept.has(s.id) ? [] : [s.id]));

  if (patches.length === 0 && inserts.length === 0 && deletes.length === 0) return { kind: "nothing" };
  return { kind: "write", patches, inserts, deletes };
}

/**
 * What the window says once the write has answered. One sentence, in the coach's terms.
 *
 * ⚠️ **A REMOVAL IS NAMED SEPARATELY AND NEVER FOLDED INTO A TOTAL.** "Saved 4 sets" over a save
 * that DELETED one of them is the flattering reading of a destructive act; a coach who removed a
 * set by accident has to be told a set was removed.
 */
export function describeSetSave(
  done: { saved: number; added: number; removed: number },
  error: { message: string } | null,
): string {
  if (error !== null) return `Not saved — ${error.message}`;
  const parts: string[] = [];
  const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;
  if (done.saved > 0) parts.push(`Saved ${plural(done.saved, "set", "sets")}`);
  if (done.added > 0) parts.push(`added ${plural(done.added, "set", "sets")}`);
  if (done.removed > 0) parts.push(`removed ${plural(done.removed, "set", "sets")}`);
  if (parts.length === 0) return "Nothing changed.";
  return `${parts.join(", ")}.`;
}

/** How a stored row arrives in the boxes. `null` is an EMPTY box, never a `0`. */
export function draftFromStored(s: SetStored): SetDraftRow {
  const show = (v: number | null) => (v === null ? "" : String(v));
  return {
    key: s.id, id: s.id,
    reps: show(s.reps), weightKg: show(s.weight_kg),
    restSeconds: show(s.rest_seconds), timeSeconds: show(s.time_seconds),
    setType: s.set_type,
  };
}

/**
 * A row the coach has just added — a COPY of the last one, which is the builder's own rule and
 * the 94.7% finding applied to the act of adding.
 *
 * ⚠️ **IT COPIES `set_type` TOO.** An exercise whose only rows are warm-ups would otherwise gain
 * a `regular` row the coach never chose, and this editor has no control for that column.
 */
export function draftNewRow(last: SetDraftRow | undefined, key: string): SetDraftRow {
  if (last === undefined) {
    return { key, id: null, reps: "", weightKg: "", restSeconds: "", timeSeconds: "", setType: "regular" };
  }
  return { ...last, key, id: null };
}
